import { useState, useEffect, Dispatch, SetStateAction } from 'react';

import { type CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { Country } from 'react-easyrocket';
import { getMaskPlaceholder } from '../../utils/getMaskPlaceholder';
import { getDefaultSelectedCountry } from '../../utils/getDefaultSelectedCountry';

interface UsePhoneNumberInput {
  countryCode: CountryCode;
  phone: string;
  isLoading: boolean;
  disabled: boolean;
}

interface UsePhoneNumberOutput {
  phoneNumber: string | undefined;
  selectedCountry: Country | undefined;
  currentCountryCode: CountryCode;
  handleSelectCountry: (country: Country) => void;
  handleBlur: () => void;
  handleFocus: () => void;
  setPhoneNumber: Dispatch<SetStateAction<string | undefined>>;
}

export const usePhoneNumber = ({
  countryCode,
  phone,
  isLoading,
  disabled
}: UsePhoneNumberInput): UsePhoneNumberOutput => {
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(() =>
    getMaskPlaceholder(countryCode)
  );
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>(() =>
    getDefaultSelectedCountry(countryCode)
  );

  const currentCountryCode = selectedCountry?.alpha2 as CountryCode;

  const handleSelectCountry = (country: Country) => {
    setSelectedCountry(country);
    setPhoneNumber(getMaskPlaceholder(country.alpha2 as CountryCode));
  };

  const handleBlur = (): void => {
    const parsedNumber = parsePhoneNumberFromString(phoneNumber || '', currentCountryCode);
    if (parsedNumber && parsedNumber.isValid()) {
      setPhoneNumber(parsedNumber.formatInternational());
      return;
    }

    if (phoneNumber && parsedNumber) {
      setPhoneNumber(phoneNumber);
      return;
    }
    setPhoneNumber(getMaskPlaceholder(currentCountryCode));
  };

  const handleFocus = (): void => {
    if (phoneNumber !== getMaskPlaceholder(currentCountryCode)) {
      return;
    }
    setPhoneNumber(selectedCountry?.phoneCodes[0]);
  };

  useEffect(() => {
    if (phone && !isLoading && disabled) {
      const parsePhone = parsePhoneNumberFromString(`+${phone}`);

      if (parsePhone?.number && parsePhone?.country) {
        const formattedNumber =
          parsePhoneNumberFromString(
            parsePhone.number,
            parsePhone.country
          )?.formatInternational() || '';
        setSelectedCountry(() => getDefaultSelectedCountry(parsePhone.country as CountryCode));
        setPhoneNumber(formattedNumber);
      }
    }
    if (!phone && !isLoading) {
      setPhoneNumber(() => getMaskPlaceholder(countryCode));
      setSelectedCountry(() => getDefaultSelectedCountry(countryCode));
    }
  }, [isLoading, phone]);

  return {
    phoneNumber,
    selectedCountry,
    currentCountryCode,
    handleSelectCountry,
    handleBlur,
    handleFocus,
    setPhoneNumber
  };
};
